import { useState, useEffect } from "react";
import { getAntiAbuseConfig, updateAntiAbuseConfig, getTenantAntiAbuseConfig, listTenants, apiFetch } from "../lib/api";

interface Defaults {
  lite: number;
  pro: number;
  resetMin: number;
}

interface TenantRow {
  tenant_id: string;
  plan_tier: string;
  config: { limit: number; resetMin: number };
}

/* W-08（2026-10-18 Captain 拍板）：現網權威值＝Lite 10／Pro 15／reset 30（D1 platform_config 實值；
   「統一 5/8」spec 52408d1 作廢）——表單初始值與載入前顯示不再用舊 5/8 誤導 */
interface VerdictRow {
  id: number;
  tenant_id: string | null;
  status: string;
  from_number: string | null;
  error_detail: string | null;
  created_at: number;
}

export default function AntiAbusePage() {
  const [defaults, setDefaults] = useState<Defaults>({ lite: 10, pro: 15, resetMin: 30 });
  const [form, setForm] = useState<Defaults>({ lite: 10, pro: 15, resetMin: 30 });
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [verdicts, setVerdicts] = useState<VerdictRow[]>([]);
  const [verdictsLoading, setVerdictsLoading] = useState(false);

  async function loadVerdicts() {
    setVerdictsLoading(true);
    try {
      const res = await apiFetch("/admin/webhook-logs?status=abuse_silent&limit=20");
      const bl = await apiFetch("/admin/webhook-logs?status=blacklisted&limit=20");
      const a = await res.json().catch(() => ({ logs: [] }));
      const b = await bl.json().catch(() => ({ logs: [] }));
      setVerdicts([...(b.logs || []), ...(a.logs || [])].slice(0, 30));
    } catch {
      setVerdicts([]);
    } finally {
      setVerdictsLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    loadVerdicts();
  }, []);

  async function loadAll() {
    setLoading(true);
    setError("");
    try {
      const [cfgRes, tenantsRes] = await Promise.all([getAntiAbuseConfig(), listTenants()]);
      if (cfgRes.success && cfgRes.defaults) {
        setDefaults(cfgRes.defaults);
        setForm(cfgRes.defaults);
      }
      if (tenantsRes.success && Array.isArray(tenantsRes.tenants)) {
        const rows = await Promise.all(
          tenantsRes.tenants.map(async (t: { tenant_id: string }) => {
            const r = await getTenantAntiAbuseConfig(t.tenant_id);
            return {
              tenant_id: t.tenant_id,
              plan_tier: r.config ? (r.tenantId === t.tenant_id ? undefined : "") : "",
              config: r.config,
            } as TenantRow;
          })
        );
        // 補 plan_tier
        const withTier = rows.map((r, i) => ({
          ...r,
          plan_tier: tenantsRes.tenants[i].plan_tier || "—",
        }));
        setTenants(withTier);
      }
    } catch {
      setError("載入失敗");
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setMessage("");
    setError("");
    try {
      const data = await updateAntiAbuseConfig({
        lite: Number(form.lite),
        pro: Number(form.pro),
        resetMin: Number(form.resetMin),
      });
      if (data.success) {
        setMessage(`已更新 ${data.updated} 項平台預設`);
        setDefaults(form);
        // 刷新各租戶生效值
        await loadAll();
      } else {
        setError(data.error || "更新失敗");
      }
    } catch {
      setError("儲存失敗");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-100 mb-6">🛡️ 反濫用上限</h1>

      {message && (
        <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-400 text-sm">
          {error}
        </div>
      )}

      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 mb-6">
        <h2 className="text-lg font-semibold text-emerald-400 mb-4">平台預設</h2>
        <p className="text-sm text-slate-400 mb-4">
          每個 WhatsApp session 內，assistant 回覆達上限即自動轉真人。閒置超過設定分鐘數後計數歸零。
          個別租戶可在「功能模塊」頁以 <code>anti_abuse_limit</code> 的額度覆蓋。
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Lite 上限（次）</label>
            <input
              type="number"
              min={1}
              value={form.lite}
              onChange={(e) => setForm((p) => ({ ...p, lite: Number(e.target.value) }))}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Pro / legacy 上限（次）</label>
            <input
              type="number"
              min={1}
              value={form.pro}
              onChange={(e) => setForm((p) => ({ ...p, pro: Number(e.target.value) }))}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">閒置重置（分鐘）</label>
            <input
              type="number"
              min={1}
              value={form.resetMin}
              onChange={(e) => setForm((p) => ({ ...p, resetMin: Number(e.target.value) }))}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-emerald-500"
            />
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
        >
          {saving ? "儲存中…" : "儲存平台預設"}
        </button>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 mb-6">
        <div className="flex items-center gap-3 mb-2">
          <h2 className="text-lg font-semibold text-emerald-400">L2 判決記錄（最近 30 筆）</h2>
          <button onClick={loadVerdicts} disabled={verdictsLoading} className="rounded-lg bg-slate-700 px-3 py-1 text-xs text-slate-200 hover:bg-slate-600">🔄</button>
        </div>
        <p className="text-sm text-slate-400 mb-3">
          同文重複閘（1 小時同文第 4 則起靜音）＋黑名單（濫用分 4 分／24h）判決帶計數；計數隨閒置重置（{form.resetMin} 分鐘）與 TTL 自然歸零。
        </p>
        {verdictsLoading ? (
          <div className="text-slate-400 text-sm">載入中…</div>
        ) : verdicts.length === 0 ? (
          <div className="text-slate-500 text-sm">近期無 L2 靜音／黑名單判決（正常情況）</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-400 uppercase border-b border-slate-700">
                <tr>
                  <th className="py-2 px-3">時間</th>
                  <th className="py-2 px-3">租戶</th>
                  <th className="py-2 px-3">號碼</th>
                  <th className="py-2 px-3">判決</th>
                  <th className="py-2 px-3">詳情（計數）</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                {verdicts.map((v) => (
                  <tr key={v.id} className="border-b border-slate-800">
                    <td className="py-2 px-3 whitespace-nowrap">{new Date(v.created_at < 1e12 ? v.created_at * 1000 : v.created_at).toLocaleString("zh-HK")}</td>
                    <td className="py-2 px-3 font-mono">{v.tenant_id || "—"}</td>
                    <td className="py-2 px-3 font-mono">{v.from_number || "—"}</td>
                    <td className="py-2 px-3">
                      {v.status === "blacklisted" ? (
                        <span className="px-2 py-0.5 text-xs bg-rose-500/20 text-rose-400 rounded">黑名單 24h</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded">靜音</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-xs text-slate-400">{v.error_detail || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
        <h2 className="text-lg font-semibold text-emerald-400 mb-4">各租戶生效值</h2>
        {loading ? (
          <div className="text-slate-400 text-sm">載入中…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-400 uppercase border-b border-slate-700">
                <tr>
                  <th className="py-2 px-3">租戶</th>
                  <th className="py-2 px-3">方案</th>
                  <th className="py-2 px-3">生效上限</th>
                  <th className="py-2 px-3">重置分鐘</th>
                  <th className="py-2 px-3">覆蓋</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                {tenants.map((t) => (
                  <tr key={t.tenant_id} className="border-b border-slate-800">
                    <td className="py-2 px-3 font-mono">{t.tenant_id}</td>
                    <td className="py-2 px-3">{t.plan_tier}</td>
                    <td className="py-2 px-3">{t.config?.limit ?? defaults[t.plan_tier === "lite199" ? "lite" : "pro"]}</td>
                    <td className="py-2 px-3">{t.config?.resetMin ?? defaults.resetMin}</td>
                    <td className="py-2 px-3">
                      {t.config?.limit !== defaults[t.plan_tier === "lite199" ? "lite" : "pro"] ? (
                        <span className="px-2 py-0.5 text-xs bg-sky-500/20 text-sky-400 rounded">租戶覆蓋</span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
